import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, CalendarClock, LifeBuoy, LogIn } from "lucide-react";

import { useBrand } from "../../contexts/BrandContext";
import { formatDate } from "../../utils/format";

const DEFAULT_HEADLINE = "Portal is closed";

type BookingClosedNoticeProps = {
  /** Overrides the headline; the body copy always comes from the admin setting. */
  headline?: string;
};

/**
 * Student-facing explanation shown wherever the booking window is closed.
 * The body text is whatever an admin configured, so the reason a student
 * reads here is the same string the API rejects reservations with.
 */
export const BookingClosedNotice: React.FC<BookingClosedNoticeProps> = ({
  headline = DEFAULT_HEADLINE,
}) => {
  const { bookingWindow } = useBrand();

  return (
    <div className="mx-auto w-full max-w-lg rounded-2xl border border-border bg-background p-6 shadow-sm sm:p-8">
      <div className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
        <CalendarClock className="h-6 w-6 text-primary" />
      </div>

      <h1 className="text-xl font-semibold text-foreground sm:text-2xl">
        {headline}
      </h1>

      <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
        {bookingWindow.message}
      </p>

      {bookingWindow.reopensAt && (
        <p className="mt-4 rounded-lg border border-border bg-muted px-3.5 py-3 text-sm text-foreground">
          Applications reopen on{" "}
          <span className="font-semibold">
            {formatDate(bookingWindow.reopensAt)}
          </span>
          .
        </p>
      )}

      <div className="mt-6 space-y-3 border-t border-border pt-5">
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          You can still
        </p>

        {bookingWindow.signInOpen && (
          <Link
            to="/login"
            className="flex items-center gap-3 rounded-lg border border-border px-4 py-3 transition-colors hover:bg-muted"
          >
            <LogIn className="h-4 w-4 shrink-0 text-primary" />
            <span className="text-sm font-medium text-foreground">
              Sign in to your account
              <span className="block text-xs font-normal text-muted-foreground">
                View your existing bookings and payment status
              </span>
            </span>
          </Link>
        )}

        <Link
          to="/support"
          className="flex items-center gap-3 rounded-lg border border-border px-4 py-3 transition-colors hover:bg-muted"
        >
          <LifeBuoy className="h-4 w-4 shrink-0 text-primary" />
          <span className="text-sm font-medium text-foreground">
            Contact support
            <span className="block text-xs font-normal text-muted-foreground">
              Get help with a booking, payment or your account
            </span>
          </span>
        </Link>

        <Link
          to="/"
          className="flex items-center gap-3 rounded-lg border border-border px-4 py-3 transition-colors hover:bg-muted"
        >
          <ArrowLeft className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="text-sm font-medium text-foreground">
            Return to home page
            <span className="block text-xs font-normal text-muted-foreground">
              Explore hostel photos, amenities and information
            </span>
          </span>
        </Link>
      </div>
    </div>
  );
};

/**
 * Slim strip for pages that stay browsable while closed (e.g. the landing
 * page), so students learn the status without having to click a CTA first.
 */
export const BookingClosedBanner: React.FC = () => {
  const { bookingWindow } = useBrand();

  if (bookingWindow.status !== "CLOSED") return null;

  return (
    <div className="border-b border-border bg-muted">
      <div className="mx-auto flex max-w-7xl flex-col gap-1.5 px-4 py-3 sm:flex-row sm:items-center sm:justify-between lg:px-8">
        <p className="flex items-start gap-2 text-sm text-foreground">
          <CalendarClock className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          <span>
            {bookingWindow.message}
            {bookingWindow.reopensAt && (
              <span className="text-muted-foreground">
                {" "}
                Reopens {formatDate(bookingWindow.reopensAt)}.
              </span>
            )}
          </span>
        </p>
        <Link
          to="/support"
          className="shrink-0 text-sm font-semibold text-primary underline-offset-2 hover:underline"
        >
          Contact support
        </Link>
      </div>
    </div>
  );
};

/** Full-page variant used where the closed window replaces the whole flow. */
export const BookingClosedPage: React.FC<BookingClosedNoticeProps> = (props) => (
  <div className="flex min-h-screen items-center justify-center bg-muted/40 px-4 py-12">
    <BookingClosedNotice {...props} />
  </div>
);

export default BookingClosedNotice;
